"use client";

import React from "react";
import { SearchBar } from "./search/search-bar";
import { UserDropdown } from "./user-dropdown";
import { NavDropdown } from "./nav-dropdown";
import { Button } from "./ui/button";
import { LogIn, Home, LayoutGrid, CirclePlus } from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import { Session } from "@/lib/user";
import Link from "next/link";
import Image from "next/image";
import { Montserrat } from "next/font/google";
import { Badge } from "./ui/badge";
import { ModeToggle } from "./mode-toggle";

const montserrat = Montserrat({
  variable: "--font-montserrat",
  subsets: ["latin"],
});

const navItems = [
  {
    title: "Home",
    url: "/",
    icon: Home,
  },
  {
    title: "Browse",
    url: "/browse",
    icon: LayoutGrid,
  },
  {
    title: "Create",
    url: "/create",
    icon: CirclePlus,
  },
];

const DesktopNavbar = ({
  session,
  hideBack,
}: {
  session: Session | null;
  hideBack?: boolean;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const isBitePage = pathname?.startsWith("/bite/");

  return (
    <div className="flex flex-row gap-4 items-center lg:items-start w-full justify-between absolute z-50 px-6 py-4 navbar-container pwa-navbar">
      {/* Left side - Logo and Navigation */}
      <div className="flex items-center gap-6 mt-1">
        {/* Logo */}
        {isBitePage ? (
          <>
            <Link
              href="/"
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-black/55 backdrop-blur-md text-white hover:bg-black/70 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background transition-colors"
              draggable={false}
              aria-label="Go home"
            >
              <Home className="h-5 w-5" />
            </Link>
            <Link
              href="/"
              className="hidden lg:flex items-center gap-2 select-none rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background transition-colors"
              draggable={false}
            >
              <div className="flex aspect-square size-8 items-center justify-center">
                <Image
                  src="/logo/icon.svg"
                  alt={"Logo"}
                  width={32}
                  height={32}
                  className="size-8"
                  draggable={false}
                />
              </div>
              <h3
                className={`text-xl font-bold ${montserrat.className} text-foreground hidden sm:block`}
              >
                Brief
              </h3>
              <Badge className="text-[0.7rem] px-1.5 h-5 bg-primary/10 text-primary hover:bg-primary/20 hidden sm:inline-flex transition duration-500 ease-in-out">
                Beta
              </Badge>
            </Link>
          </>
        ) : (
          <Link
            href="/"
            className="flex items-center gap-2 select-none rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background transition-colors"
            draggable={false}
          >
            <div className="flex aspect-square size-10 lg:size-8 items-center justify-center">
              <Image
                src="/logo/icon.svg"
                alt={"Logo"}
                width={32}
                height={32}
                className="size-10 lg:size-8"
                draggable={false}
              />
            </div>
            <h3
              className={`text-xl font-bold ${montserrat.className} text-foreground hidden sm:block`}
            >
              Brief
            </h3>
            <Badge className="text-[0.7rem] px-1.5 h-5 bg-primary/10 text-primary hover:bg-primary/20 hidden sm:inline-flex transition duration-500 ease-in-out">
              Beta
            </Badge>
          </Link>
        )}

        {/* Desktop Navigation - Only show on medium screens and up */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <Button
              key={item.title}
              variant="ghost"
              size="sm"
              className={`gap-2 hover:bg-accent/75 ${pathname === item.url ? "bg-accent/75" : ""}`}
              asChild
            >
              <Link href={item.url}>
                <item.icon className="h-4 w-4" />
                {item.title}
              </Link>
            </Button>
          ))}
        </nav>
      </div>

      {/* Center - Search Bar */}
      <div className="hidden lg:flex w-full justify-end">
        <div className="flex-1 max-w-md">
          <SearchBar />
        </div>
      </div>

      {/* Right side - User actions and mobile menu */}
      <div className="flex gap-2 items-center">
        <div className="lg:hidden">
          <SearchBar collapsedOnMobile heroStyle={Boolean(isBitePage)} />
        </div>

        {/* Mobile menu dropdown - Only show on mobile */}
        <div className="lg:hidden">
          <NavDropdown heroStyle={Boolean(isBitePage)} />
        </div>

        {session?.user ? (
          <UserDropdown
            user={{
              name: session?.user?.name!,
              email: session?.user?.email!,
              avatar: session?.user?.image!,
              subscriptionStatus: session?.user?.subscriptionStatus!,
              daysLeftInSubscription: session?.user?.daysLeftInSubscription!,
              preferredReadingMode: session?.user?.preferredReadingMode,
            }}
          />
        ) : (
          <div className="hidden lg:flex lg:items-center lg:gap-2">
            <ModeToggle className="mt-1" />
            <Button
              onClick={() => router.push("/login")}
              variant="outline"
              className="gap-2 mt-1 h-10 w-10 sm:w-auto"
            >
              <LogIn className="h-4 w-4" />
              <span className="hidden sm:inline">Sign In</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopNavbar;
